import React from 'react';
import FoldDiagram from './FoldDiagram';
import { calculateUnfoldedDimensions, calculateFoldedDimensions } from './foldingUtils'; // Add this line

const LeafletOptionsForm = ({
  foldType,
  setFoldType,
  width,
  height,
  isDoubleSided,
  standardSize,
  dimensionInputType,
  setDimensionInputType
}) => {
  // NEW: Calculate dimensions properly based on input type
  const getCalculatedDimensions = () => {
    if (!foldType || width <= 0 || height <= 0) {
      return {
        finishedWidth: width,
        finishedHeight: height,
        unfoldedWidth: width,
        unfoldedHeight: height
      };
    }

    let finishedWidth, finishedHeight, unfoldedWidth, unfoldedHeight;

    if (dimensionInputType === 'finished') {
      // Input is finished size, calculate unfolded
      finishedWidth = width;
      finishedHeight = height;
      const unfoldedDims = calculateUnfoldedDimensions(width, height, foldType);
      unfoldedWidth = unfoldedDims.unfoldedWidthMm;
      unfoldedHeight = unfoldedDims.unfoldedHeightMm;
    } else {
      // Input is unfolded size, calculate finished
      unfoldedWidth = width;
      unfoldedHeight = height;
      const foldedDims = calculateFoldedDimensions(width, height, foldType);
      finishedWidth = foldedDims.width;
      finishedHeight = foldedDims.height;
    }

    return { finishedWidth, finishedHeight, unfoldedWidth, unfoldedHeight };
  };

  const { finishedWidth, finishedHeight, unfoldedWidth, unfoldedHeight } = getCalculatedDimensions();

  // Calculate panel count based on fold type
  const getPanelCount = () => {
    if (!foldType) return 1;
    let panelCount = 1;
    switch(foldType) {
      case 'half': panelCount = 2; break;
      case 'tri': case 'z': panelCount = 3; break;
      case 'gate': panelCount = 3; break;
      case 'cross': panelCount = 4; break;
      case 'accordion': panelCount = 4; break;
      case 'parallel': panelCount = 2; break;
      default: panelCount = 1;
    }
    return panelCount;
  };

  // Get the effective page count based on fold type and whether it's double-sided
  const getEffectivePageCount = () => {
    if (!foldType) return isDoubleSided ? 2 : 1;
    const panelCount = getPanelCount();
    return isDoubleSided ? panelCount * 2 : panelCount;
  };
  
  const effectivePageCount = getEffectivePageCount();
  const currentPanelCount = getPanelCount();
  
  // Helper function to format dimensions
  const formatDimensions = (w, h) => `${Math.round(w)}mm × ${Math.round(h)}mm`;
  
  return (
    <div className="md:col-span-2 border p-4 rounded bg-gray-50">
      <h3 className="font-medium mb-3">Leaflet Options</h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Fold Type</label>
        <select
          value={foldType || ''}
          onChange={(e) => setFoldType(e.target.value)}
          className="w-full p-2 border rounded"
        >
          <option value="">No folding</option>
          <option value="half">Half Fold</option>
          <option value="tri">Tri-Fold</option>
          <option value="z">Z-Fold</option>
          <option value="gate">Gate Fold</option>
          <option value="cross">Cross Fold</option>
          <option value="accordion">Accordion Fold</option>
          <option value="parallel">Parallel Fold</option>
        </select>
      </div>
      
      {/* Dimension Input Type Toggle */}
      {foldType && (
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200 text-sm">
          <label className="block font-medium mb-1 text-blue-800">Input Dimensions As:</label>
          <div className="flex gap-4">
            <div className="flex items-center">
              <input
                type="radio"
                id="input-finished"
                name="dimensionInputType"
                value="finished"
                checked={dimensionInputType === 'finished'}
                onChange={() => setDimensionInputType('finished')}
                className="mr-1"
              />
              <label htmlFor="input-finished">Finished Size</label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="input-unfolded"
                name="dimensionInputType"
                value="unfolded"
                checked={dimensionInputType === 'unfolded'}
                onChange={() => setDimensionInputType('unfolded')}
                className="mr-1"
              />
              <label htmlFor="input-unfolded">Unfolded Size</label>
            </div>
          </div>
        </div>
      )}

      {foldType && (
        <div className="mt-4 bg-blue-50 p-3 rounded border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Folding Details</h4>
          
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between">
              <span className="font-medium">Input Size:</span>
              <span>
                {formatDimensions(width, height)} 
                {standardSize && ` (${standardSize.toUpperCase()})`} 
                ({dimensionInputType === 'finished' ? 'Finished' : 'Unfolded'})
              </span>
            </div>

            <div className="flex justify-between">
              <span className="font-medium">Effective Page Count:</span>
              <span>{effectivePageCount} {effectivePageCount === 1 ? 'page' : 'pages'}</span>
            </div>
            
            {foldType && (
              <div className="flex justify-between">
                <span className="font-medium">Total Panels:</span>
                <span>{currentPanelCount}</span>
              </div>
            )}
          </div>

          {/* Display Both Calculated Finished and Unfolded Sizes */}
          <div className="grid grid-cols-1 gap-2 text-sm mt-2 pt-2 border-t border-blue-300">
            <div className="flex justify-between">
              <span className="font-medium">Finished Size:</span>
              <span className="text-green-700 font-medium">
                {formatDimensions(finishedWidth, finishedHeight)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Unfolded Size:</span>
              <span className="text-blue-700 font-medium">
                {formatDimensions(unfoldedWidth, unfoldedHeight)}
              </span>
            </div>
          </div>

          <div className="mt-3 text-xs text-blue-700">
            <p><strong>Pricing note:</strong> Material costs and printing are calculated based on the unfolded size ({formatDimensions(unfoldedWidth, unfoldedHeight)}), but your finished product will be {formatDimensions(finishedWidth, finishedHeight)}.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeafletOptionsForm;